import { GetObjectCommand, HeadObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { isNil } from '@lucidlink/tscore';
import { fetchHttpFileMetadata, getFetchErrorMessage, HTTP_STATUS_FORBIDDEN, HttpMetadataNetworkError, sanitizeUrl, } from '#object-store/common';
import { BucketNotFoundError, ObjectNotFoundError, ObjectStoreCredentialsKind, ObjectStoreError, PresignedUrlError, PresignedUrlUnreachableError, } from '#object-store/types';
import { createS3ClientConfig } from './client-config';
import { IamCredentialsProvider } from './iam-credentials-provider';
import { DEFAULT_RETRY_CONFIG, RetryStrategy } from './retry';
import { createS3AuthorizationError } from './s3-authorization-error';
export const createPresignedUrlGenerator = (config) => {
    return new PresignedUrlGeneratorImpl(config);
};
class PresignedUrlGeneratorImpl {
    s3Client;
    bucketName;
    constructor(config) {
        if (config.credentials.kind !== ObjectStoreCredentialsKind.IAM) {
            throw new ObjectStoreError('Presigned URL generation requires IAM credentials');
        }
        const s3Config = createS3ClientConfig({
            ...config,
            credentialsProvider: new IamCredentialsProvider(config.credentials),
            retryStrategy: RetryStrategy.DEFAULT,
            retryOptions: DEFAULT_RETRY_CONFIG,
        });
        this.s3Client = new S3Client(s3Config);
        this.bucketName = config.bucketName;
    }
    async generate(objectId, expiresIn, metadata) {
        const url = await this.presign(new GetObjectCommand({ Bucket: this.bucketName, Key: objectId }), expiresIn);
        if (!isNil(metadata)) {
            return { url, ...metadata };
        }
        const headUrl = await this.presign(new HeadObjectCommand({ Bucket: this.bucketName, Key: objectId }), expiresIn);
        return { url, ...(await this.getMetadata(url, headUrl, objectId)) };
    }
    async presign(command, expiresIn) {
        try {
            return await getSignedUrl(this.s3Client, command, { expiresIn });
        }
        catch (error) {
            throw new PresignedUrlError(`Failed to generate presigned URL: ${getFetchErrorMessage(error)}`);
        }
    }
    async throwS3ResponseError(res, objectId) {
        const bodyText = await res.text().catch(() => '');
        const s3ErrorCode = bodyText.match(/<Code>([^<]+)<\/Code>/)?.[1];
        const s3ErrorMessage = bodyText.match(/<Message>([^<]+)<\/Message>/)?.[1];
        if (s3ErrorCode === 'NoSuchBucket') {
            throw new BucketNotFoundError(this.bucketName);
        }
        if (s3ErrorCode === 'NoSuchKey') {
            throw new ObjectNotFoundError(objectId);
        }
        if (res.status === HTTP_STATUS_FORBIDDEN) {
            const errorDetails = this.formatS3Error(s3ErrorCode, s3ErrorMessage);
            throw createS3AuthorizationError(`Access denied for object ${objectId}${errorDetails ? `: ${errorDetails}` : ''}`, s3ErrorCode);
        }
        const errorDetails = this.formatS3Error(s3ErrorCode, s3ErrorMessage);
        throw new PresignedUrlError(`Failed to fetch object metadata: ${res.status}:${res.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`);
    }
    formatS3Error(code, message) {
        if (code && message) {
            return `${code} - ${message}`;
        }
        return code ?? message ?? '';
    }
    async getMetadata(url, headUrl, objectId) {
        const { size, checksum } = await this.fetchMetadataOrThrow(url, headUrl, objectId);
        if (isNil(checksum)) {
            throw new PresignedUrlError('Failed to get checksum');
        }
        return { size, checksum };
    }
    async fetchMetadataOrThrow(url, headUrl, objectId) {
        try {
            return await fetchHttpFileMetadata(url, {
                headUrl,
                onUnexpectedHttpStatus: (response) => {
                    return this.throwS3ResponseError(response, objectId);
                },
            });
        }
        catch (error) {
            if (error instanceof ObjectStoreError || error instanceof PresignedUrlError) {
                throw error;
            }
            const message = error instanceof Error ? error.message : 'Unknown error';
            const errorMessage = `Failed to fetch object metadata from ${sanitizeUrl(url)}: ${message}`;
            if (error instanceof HttpMetadataNetworkError) {
                throw new PresignedUrlUnreachableError(errorMessage);
            }
            throw new PresignedUrlError(errorMessage);
        }
    }
}
